/**
 * CollapsibleCardHeader: the header of a card that opens and closes
 * (Stock Sync > Configuration's Push Configuration, Approval Queue, Push
 * Exclusions, Skip or Always 0 and Variant Pre-Discovery).
 *
 * Use it inside a `<Collapsible>` wrapping a `<Card>`. The whole header is the
 * hit area, but the control is a real `<button type="button">` inside the
 * title (the disclosure pattern: `<h3><button aria-expanded>`), never a `div`
 * given a button role. A `div` trigger is not reachable by Tab and Enter /
 * Space do nothing on it. `type="button"` matters because these cards sit
 * inside a settings `<form>`: a default button would submit it.
 *
 * The button's `::after` stretches over the header, so a click on the icon,
 * the description or a count badge toggles too, and the focus ring is drawn
 * on that stretched box, so keyboard focus outlines the whole header.
 * Everything in `aside` and `children` therefore sits under the button: keep
 * them non-interactive (badges, a note). A control there could not be clicked.
 */
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface CollapsibleCardHeaderProps {
  /** Whether the card is open; turns the chevron. */
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** The tile before the title (a lucide icon in a tinted square). Decorative. */
  icon?: ReactNode;
  /** Right-hand cluster before the chevron (count badges). Non-interactive. */
  aside?: ReactNode;
  /** Under the description (a `NoteLine`). Non-interactive. */
  children?: ReactNode;
  className?: string;
}

export function CollapsibleCardHeader({
  open,
  title,
  description,
  icon,
  aside,
  children,
  className,
}: CollapsibleCardHeaderProps) {
  return (
    <CardHeader className={cn('relative', className)}>
      <div className="flex flex-wrap items-center gap-3">
        {icon}
        <div className="min-w-0 flex-1 space-y-1">
          <CardTitle className="text-base">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(
                  'cursor-pointer text-left outline-none',
                  // The stretched hit area and the focus ring around the whole header.
                  'after:absolute after:inset-0 after:rounded-lg',
                  'focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-offset-2',
                )}
              >
                {title}
              </button>
            </CollapsibleTrigger>
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
          {children}
        </div>
        {aside}
        <ChevronDown
          className={cn(
            'ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>
    </CardHeader>
  );
}
